import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.ts";

export const AccountActivationPage = () => {
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [hasInviteSession, setHasInviteSession] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    let mounted = true;

    async function checkSession() {
      if (!supabase) {
        if (mounted) {
          setError("De accountactivatie is tijdelijk niet beschikbaar.");
          setChecking(false);
        }
        return;
      }

      const hash = new URLSearchParams(
        window.location.hash.startsWith("#")
          ? window.location.hash.slice(1)
          : window.location.hash
      );

      const hashError =
        hash.get("error_description") || hash.get("error");

      if (hashError) {
        if (mounted) {
          setError(
            "Deze uitnodigingslink is niet meer geldig. Vraag Pippaya om een nieuwe uitnodiging."
          );
          setChecking(false);
        }
        return;
      }

      const { data, error: sessionError } =
        await supabase.auth.getSession();

      if (!mounted) return;

      if (sessionError) {
        setError(
          "De uitnodiging kon niet worden gecontroleerd. Open de link opnieuw vanuit de e-mail."
        );
        setChecking(false);
        return;
      }

      setHasInviteSession(!!data.session?.user);
      setChecking(false);
    }

    checkSession();

    const { data: listener } = supabase?.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted) return;
        if (session?.user) {
          setHasInviteSession(true);
          setChecking(false);
        }
      }
    ) ?? { data: null as any };

    return () => {
      mounted = false;
      listener?.subscription?.unsubscribe();
    };
  }, []);

  async function activateAccount(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!supabase) {
      setError("De accountactivatie is tijdelijk niet beschikbaar.");
      return;
    }

    if (password.length < 8) {
      setError("Kies een wachtwoord van minimaal 8 tekens.");
      return;
    }

    if (password !== passwordAgain) {
      setError("De twee wachtwoorden zijn niet gelijk.");
      return;
    }

    setBusy(true);

    const { error: updateError } =
      await supabase.auth.updateUser({ password });

    if (updateError) {
      setError(
        "Het wachtwoord kon niet worden opgeslagen. Probeer de uitnodigingslink opnieuw of neem contact op met Pippaya."
      );
      setBusy(false);
      return;
    }

    setSuccess("Je account is geactiveerd. Je gaat nu naar Mijn Pippaya.");

    window.setTimeout(() => {
      navigate("/mijn-pippaya", { replace: true });
    }, 900);
  }

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="sm">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3
          }}
        >
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Pippaya Hondenopvang
              </Typography>
              <Typography variant="h4" fontWeight={800}>
                Welkom bij Mijn Pippaya
              </Typography>
            </Box>

            {checking && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={22} />
                <Typography>Uitnodiging controleren…</Typography>
              </Stack>
            )}

            {!checking && error && (
              <Alert severity="error">{error}</Alert>
            )}

            {!checking && !hasInviteSession && !error && (
              <Alert severity="info">
                Open deze pagina via de persoonlijke uitnodigingslink in de
                e-mail van Pippaya. Daarmee wordt je account veilig geactiveerd.
              </Alert>
            )}

            {!checking && hasInviteSession && (
              <Box component="form" onSubmit={activateAccount}>
                <Stack spacing={2}>
                  <Typography color="text.secondary">
                    Kies hieronder je wachtwoord. Daarna kun je inloggen in
                    Mijn Pippaya en je eigen honden en reserveringen bekijken.
                  </Typography>

                  <TextField
                    label="Nieuw wachtwoord"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    disabled={busy}
                  />

                  <TextField
                    label="Herhaal wachtwoord"
                    type="password"
                    value={passwordAgain}
                    onChange={e => setPasswordAgain(e.target.value)}
                    autoComplete="new-password"
                    fullWidth
                    disabled={busy}
                  />

                  {error && <Alert severity="error">{error}</Alert>}

                  {success && (
                    <Alert
                      severity="success"
                      icon={<CheckCircleRoundedIcon />}
                    >
                      {success}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={busy || !!success}
                  >
                    {busy ? "Account activeren…" : "Account activeren"}
                  </Button>
                </Stack>
              </Box>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
};
